<script lang="ts">
  import { fileStore } from "../../services/select-files";
  import PreviewDataComponent from "../PreviewDataComponent.svelte";
  import type { DataFile, DataSearchResponse } from "../../models/data.interface";
  import TooltipHybridButton from "./TooltipHybridButton.svelte";

  export let searchResponse: DataSearchResponse;
  export let datasetMetadata;

  let dataPath = new URL(process.env.PREVIEW_DATA_URL);
  let previewFile: DataFile;
  let previewDataServiceUrl: string;

  function transform(input: number): string {
    let transformedValue = "";
    if (input != null) {
      if (input.toString().length >= 3 && input.toString().length <= 6) {
        transformedValue = `${(input / 1000).toFixed(2)} KB`;
      } else if (input.toString().length >= 6 && input.toString().length <= 9) {
        transformedValue = `${(input / 1000000).toFixed(2)} MB`;
      } else if (input.toString().length >= 9 && input.toString().length <= 12) {
        transformedValue = `${(input / 1000000000).toFixed(2)} GB`;
      } else {
        transformedValue = `${(input / 1000000000000).toFixed(2)} TB`;
      }
    }
    return transformedValue;
  }

  function transformDate(input: string): string {
    return input ? input.slice(0, 10) : "undefined";
  }

  function showPreview(file: DataFile): void {
    previewFile = file;
  }

  function getPreviewDataServiceUrl(selectedFile): string {
    const stationId =
      selectedFile["stations"] && selectedFile["stations"].length > 0
        ? selectedFile["stations"][0].id
        : "";
    previewDataServiceUrl =
      dataPath +
      `?dataset=${datasetMetadata.id}` +
      `&startDate=0001-01-01` +
      `&endDate=9996-12-31` +
      `&stations=${stationId}` +
      `&format=pdf`;
    return previewDataServiceUrl;
  }

  function toggleFileSelection(file: DataFile) {
    const isSelected = $fileStore.selectedFiles.some((f) => f.id === file.id);
    const isAtLimit =
      $fileStore.selectedFiles.length >= datasetMetadata?.ncei?.access?.order?.maxFileCount;

    if (isSelected) {
      fileStore.remove(file);
    } else if (isAtLimit) {
      alert("Maximum file selection limit reached");
    } else {
      fileStore.add(file);
    }
  }
</script>

{#each searchResponse?.results || [] as r, i (r.id)}
  <div class="card mb-3" data-bs-toggle="tooltip" data-bs-placement="top" title="Weather Event Description: {r.event}">
    <div class="card-body">
      <h5 class="card-title">{r.name}</h5>
      <!-- row for file data titles -->
      <div class="row card-text">
        {#if datasetMetadata.filedata.includes("periodOfRecord")}
          <div class="col fw-semibold">Period of Record</div>
        {/if}
        {#if datasetMetadata.filedata.includes("fileSize")}
          <div class="col fw-semibold">Filesize</div>
        {/if}
        {#if datasetMetadata.filedata.includes("datatypes")}
          <div class="col fw-semibold">
            {r.dataTypesCount} Datatypes
          </div>
        {/if}
        {#if datasetMetadata.filedata.includes("station")}
          <div class="col fw-semibold">
            {r.stations.length} Stations
          </div>
        {/if}
      </div>

      <!-- row for file data values -->
      <div class="row">
        {#if datasetMetadata.filedata.includes("periodOfRecord")}
          <div class="col">
            {transformDate(r.startDate)} to {transformDate(r.endDate)}
          </div>
        {/if}
        {#if datasetMetadata.filedata.includes("fileSize")}
          <div class="col">{transform(r.fileSize)}</div>
        {/if}
        {#if datasetMetadata.filedata.includes("datatypes")}
          <div class="col">
            <div class="dropdown">
              <button
                  class="btn ae-outline-secondary btn-sm dropdown-toggle"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  data-bs-offset="0,10"
              >
                Show Datatypes
              </button>
              <ul class="dropdown-menu" style="max-height: 300px; overflow-y: auto;">
                {#each r?.dataTypes.map(dt => dt.id).sort() || [] as dataTypeId}
                  <li>
                    <span class="dropdown-item-text text-muted small">
                      {dataTypeId}
                    </span>
                  </li>
                {/each}
              </ul>
            </div>
          </div>
        {/if}
        {#if datasetMetadata.filedata.includes("station")}
          <div class="col">
            <div class="dropdown">
              <button
                  class="btn ae-outline-secondary btn-sm dropdown-toggle"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  data-bs-offset="0,10"
              >
                Show Stations
              </button>
              <ul class="dropdown-menu" style="max-height: 300px; overflow-y: auto;">
                {#each r?.stations.map(s => s.id).sort() || [] as stationId}
                  <li>
                    <span class="dropdown-item-text text-muted small">
                      {stationId}
                    </span>
                  </li>
                {/each}
              </ul>
            </div>
          </div>
        {/if}
      </div>
    </div>
    <div class="card-footer bg-transparent">
      <div class="d-flex justify-content-between">
        <div>
          {#if r.filePath}
            <a class="btn ae-tertiary btn-sm" href={r.filePath} role="button">
              <i class="bi bi-download"></i>
              Download
            </a>
          {/if}
          {#if datasetMetadata?.previewoptions && (datasetMetadata?.previewoptions.dateselection || datasetMetadata?.previewoptions.stationselection)}
            <a
                class="btn ae-tertiary btn-sm"
                data-bs-toggle="collapse"
                href="#preview-{i}"
                role="button"
                aria-expanded="false"
                aria-controls="collapseExample"
                on:click={() => showPreview(r)}
            >
              <i class="bi bi-file-earmark-text"></i>
              Preview
            </a>
            <TooltipHybridButton
                searchUrlQueryParam="preview-data"
                tooltip="#result-count-limit-tooltip"
            />
            <!--            <span class="pt-2">-->
            <!--              <i class="bi bi-question-circle-fill" id="PreviewDataToolTip"></i>-->
            <!--              <Tooltip placement="right" target={`PreviewDataToolTip`}>-->
            <!--                Make a selection below and click the "Preview Data" button to be taken to a preview-->
            <!--                of the data within the selected file. Some datasets do not require any options to be-->
            <!--                selected in which case just click the "Preview Data" button-->
            <!--              </Tooltip>-->
            <!--            </span>-->
          {/if}
          {#if datasetMetadata?.previewoptions && !datasetMetadata?.previewoptions.dateselection && !datasetMetadata?.previewoptions.stationselection}
            <a
                class="btn ae-tertiary btn-sm"
                target="_blank"
                rel="noreferrer"
                on:click={() => getPreviewDataServiceUrl(r)}
                href={previewDataServiceUrl}
            >
              <i class="bi bi-file-earmark-text"></i>
              Preview
            </a>
            <TooltipHybridButton
                searchUrlQueryParam="preview-data"
                tooltip="#preview-data-tooltip"
            />
            <!--            <span class="pt-2">-->
            <!--              <i class="bi bi-question-circle-fill" id="PreviewDataToolTip"></i>-->
            <!--              <Tooltip placement="right" target={`PreviewDataToolTip`}>-->
            <!--                Make a selection below and click the "Preview Data" button to be taken to a preview-->
            <!--                of the data within the selected file. Some datasets do not require any options to be-->
            <!--                selected in which case just click the "Preview Data" button-->
            <!--              </Tooltip>-->
            <!--            </span>-->
          {/if}
        </div>
        <div>
          <button
              on:click={() => toggleFileSelection(r)}
              type="button"
              id="selectBtn"
              class="btn {$fileStore.selectedFiles.some((f) => f.id === r.id)
              ? 'ae-secondary'
              : 'ae-outline-primary'} btn-sm"
          >
            {$fileStore.selectedFiles.some((f) => f.id === r.id) ? "- Deselect" : "+ Select"}
          </button>
        </div>
      </div>
      {#if datasetMetadata?.previewoptions && (datasetMetadata?.previewoptions.dateselection || datasetMetadata?.previewoptions.stationselection)}
        <div class="d-flex">
          <div class="collapse" id="preview-{i}">
            <PreviewDataComponent {datasetMetadata} file={previewFile} />
          </div>
        </div>
      {/if}
    </div>
  </div>
{/each}

<style>

  /*:global(.high-contrast-dropdown) {*/
  /*  color: #212529 !important;*/
  /*  background-color: #ffffff !important;*/
  /*  border: 2px solid #6c757d !important;*/
  /*  font-weight: 500;*/
  /*}*/

  /*:global(.high-contrast-dropdown:hover) {*/
  /*  color: #000000 !important;*/
  /*  background-color: #f8f9fa !important;*/
  /*  border-color: #495057 !important;*/
  /*  box-shadow: 0 0 0 0.2rem rgba(108, 117, 125, 0.25);*/
  /*}*/

  /*:global(.high-contrast-dropdown:focus) {*/
  /*  color: #000000 !important;*/
  /*  background-color: #ffffff !important;*/
  /*  border-color: #495057 !important;*/
  /*  outline: 2px solid #0066cc;*/
  /*  outline-offset: 2px;*/
  /*  box-shadow: 0 0 0 0.2rem rgba(0, 102, 204, 0.25);*/
  /*}*/

  /*:global(.high-contrast-dropdown:active) {*/
  /*  color: #000000 !important;*/
  /*  background-color: #e9ecef !important;*/
  /*  border-color: #495057 !important;*/
  /*}*/

  :global(.dropdown-menu) {
    font-size: 0.875rem;
    padding: 0.5rem 0;
  }
</style>
